<template>
  <div class="container notifications-page">
    <div class="page-container">
      <!-- Header -->
      <div class="page-header">
        <div class="header-left">
          <h1 class="page-title">Notifications</h1>
          <div class="total-count">{{ total }} {{ activeTab === 'active' ? 'active' : 'archived' }} notifications</div>
        </div>
      </div>

      <!-- Filter Tabs with Select Button (hidden in selection mode) -->
      <div v-if="!selectionMode" class="filter-tabs">
        <div class="filter-tabs-left">
          <button
            :class="{ active: activeTab === 'active' }"
            @click="setTab('active')"
            class="tab-button"
          >
            <font-awesome-icon icon="bell" />
            Active
          </button>
          <button
            :class="{ active: activeTab === 'archived' }"
            @click="setTab('archived')"
            class="tab-button"
          >
            <font-awesome-icon icon="archive" />
            Archived
          </button>
        </div>
        <button
          @click="enterSelectionMode()"
          class="btn-select"
        >
          <font-awesome-icon icon="check-square" />
          Select
        </button>
      </div>

      <!-- Search Input (hidden in selection mode) -->
      <div v-if="!selectionMode" class="search-wrapper">
        <font-awesome-icon icon="magnifying-glass" class="search-icon" />
        <input
          v-model="searchInput"
          type="text"
          class="search-input"
          placeholder="Search notifications..."
          @input="handleSearchInput"
        />
      </div>

      <!-- Selection Bar (only in selection mode) -->
      <div v-if="selectionMode" class="selection-bar">
        <div class="selection-left">
          <button @click="toggleSelectAll" class="btn-select-all">
            <font-awesome-icon :icon="allCurrentPageSelected ? 'check-square' : 'square'" />
            Select All
          </button>
          <span class="selection-count">
            <span v-if="selectedCount === 0">{{ notifications.length }} on this page</span>
            <span v-else>{{ selectedCount }} of {{ notifications.length }} selected</span>
          </span>
        </div>
        <div class="bulk-actions">
          <button
            @click="bulkMarkAsRead"
            :disabled="bulkProcessing"
            class="btn-bulk-action btn-mark-read"
          >
            <font-awesome-icon v-if="bulkProcessing" icon="spinner" spin />
            <font-awesome-icon v-else icon="envelope-open" />
            Mark as Read
          </button>
          <button
            @click="bulkArchive"
            :disabled="bulkProcessing"
            class="btn-bulk-action btn-archive"
          >
            <font-awesome-icon v-if="bulkProcessing" icon="spinner" spin />
            <font-awesome-icon v-else icon="archive" />
            Archive
          </button>
          <button
            @click="exitSelectionMode"
            :disabled="bulkProcessing"
            class="btn-bulk-action btn-cancel"
          >
            Cancel
          </button>
        </div>
      </div>

      <!-- Loading State (initial load) -->
      <div v-if="loading && notifications.length === 0" class="loading-container">
        <font-awesome-icon icon="spinner" class="spinner" spin />
        <p class="loading-text">{{ searchInput ? 'Searching...' : 'Loading notifications...' }}</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-container">
        <font-awesome-icon icon="triangle-exclamation" class="error-icon" />
        <p class="error-title">Failed to load notifications</p>
        <p class="error-message">{{ error }}</p>
        <button class="retry-button" @click="resetAndSearch">
          Try Again
        </button>
      </div>

      <!-- Empty State -->
      <div v-else-if="notifications.length === 0 && !loading" class="empty-state">
        <font-awesome-icon :icon="activeTab === 'active' ? 'bell' : 'archive'" class="empty-icon" />
        <p class="empty-title">
          {{ searchInput ? 'No notifications found' : (activeTab === 'active' ? 'You\'re all caught up' : 'No archived notifications') }}
        </p>
        <p class="empty-description">
          {{ searchInput ? 'Try a different search term' : (activeTab === 'active' ? 'All your notifications will appear here' : 'Archived notifications will appear here') }}
        </p>
      </div>

      <!-- Notifications List -->
      <div v-else class="notifications-list">
        <div class="notification-grid">
          <div
            v-for="notification in notifications"
            :key="notification.id"
            class="notification-row"
            :class="{
              'selected': selectedIds.has(notification.id)
            }"
          >
            <!-- Notification Item -->
            <div
              class="notification-item"
              :class="{
                'unread': !notification.is_read && activeTab === 'active',
                'selectable': selectionMode
              }"
              @click="handleNotificationItemClick(notification)"
            >
              <div class="notification-icon">
                <font-awesome-icon
                  :icon="getNotificationIcon(notification.severity)"
                  :class="getNotificationIconClass(notification.severity)"
                />
              </div>
              <div class="notification-content">
                <div class="notification-header">
                  <span class="notification-title">{{ notification.title }}</span>
                  <span class="notification-time">{{ moment(notification.created_at).fromNow() }}</span>
                </div>
                <p v-if="notification.email_message" class="notification-message">{{ notification.email_message.replace(/\n/g, ' ') }}</p>
              </div>
              <div v-if="!notification.is_read && activeTab === 'active' && !selectionMode" class="unread-indicator"></div>

              <!-- Quick Action Buttons (only when not in selection mode) -->
              <div v-if="!selectionMode" class="notification-actions">
                <button
                  v-if="!notification.is_read"
                  @click.stop="quickMarkAsRead(notification)"
                  class="btn-quick-action"
                  title="Mark as read"
                >
                  <font-awesome-icon icon="envelope-open" />
                </button>
                <button
                  v-if="!notification.is_archived"
                  @click.stop="quickArchive(notification)"
                  class="btn-quick-action"
                  title="Archive"
                >
                  <font-awesome-icon icon="archive" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Intersection Observer Trigger -->
        <div ref="loadMoreTrigger" class="load-more-trigger"></div>

        <!-- Loading More Indicator -->
        <div v-if="loadingMore" class="loading-more">
          <font-awesome-icon icon="spinner" class="spinner-small" spin />
          <span>Loading more...</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import { useMainStore } from '@/stores/main'
import moment from 'moment'
import User from '@/api/User'
import { useToast } from 'vue-toastification'
import NotificationDetail from '@/components/SlideOver/NotificationDetail.vue'

const mainStore = useMainStore()
const toast = useToast()

// Tab state
const activeTab = ref('active')
const includeArchived = computed(() => activeTab.value === 'archived')

// State
const notifications = ref([])
const loading = ref(false)
const loadingMore = ref(false)
const error = ref('')
const searchInput = ref('')
const offset = ref(0)
const limit = 50
const total = ref(0)
const hasMore = ref(false)
const loadMoreTrigger = ref(null)
let observer = null
let searchTimeout = null

// Selection mode state
const selectionMode = ref(false)
const selectedIds = ref(new Set())
const bulkProcessing = ref(false)

// Computed properties for selection
const hasSelection = computed(() => selectedIds.value.size > 0)
const allCurrentPageSelected = computed(() => {
  if (notifications.value.length === 0) return false
  return notifications.value.every(n => selectedIds.value.has(n.id))
})
const selectedCount = computed(() => selectedIds.value.size)

// Fetch notifications from API
const fetchNotifications = async (isLoadMore = false) => {
  if (!hasMore.value && isLoadMore) return
  if (loading.value || loadingMore.value) return

  if (isLoadMore) {
    loadingMore.value = true
  } else {
    loading.value = true
  }

  error.value = ''

  try {
    const response = await User.getNotifications(offset.value, limit, searchInput.value, includeArchived.value)

    if (response.status === 'success') {
      const responseData = response.data || {}
      const notificationsArray = responseData.notifications || []

      if (isLoadMore) {
        notifications.value = [...notifications.value, ...notificationsArray]
      } else {
        notifications.value = notificationsArray
      }

      total.value = responseData.total || 0
      hasMore.value = responseData.has_more || false
      offset.value += notificationsArray.length
    } else {
      error.value = response.error || 'Failed to load notifications'
      hasMore.value = false
    }
  } catch (err) {
    console.error('Error fetching notifications:', err)
    error.value = err.response?.data?.message || 'An error occurred while loading notifications'
    hasMore.value = false
  } finally {
    loading.value = false
    loadingMore.value = false
  }
}

// Handle search input with debounce (300ms)
const handleSearchInput = () => {
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }

  searchTimeout = setTimeout(() => {
    resetAndSearch()
  }, 300)
}

// Reset offset and search
const resetAndSearch = () => {
  offset.value = 0
  notifications.value = []
  hasMore.value = true
  fetchNotifications()
}

// Set tab and reload
const setTab = (tab) => {
  if (activeTab.value !== tab) {
    activeTab.value = tab
    exitSelectionMode() // Clear selection when switching tabs
    resetAndSearch()
  }
}

// Quick mark as read (individual notification)
async function quickMarkAsRead(notification) {
  try {
    const response = await User.markNotificationRead(notification.id)
    if (response.status === 'success') {
      notification.is_read = true
      toast.success('Marked as read')

      // Update unread count
      const countResponse = await User.getUnreadNotificationCount()
      if (countResponse.status === 'success') {
        mainStore.update_notification_count(countResponse.count)
      }
    } else {
      toast.error('Failed to mark as read')
    }
  } catch (error) {
    console.error('Error marking notification as read:', error)
    toast.error('Failed to mark as read')
  }
}

// Quick archive (individual notification)
async function quickArchive(notification) {
  try {
    const response = await User.archiveNotification(notification.id)
    if (response.status === 'success') {
      toast.success('Archived')
      // Refresh the list
      resetAndSearch()
    } else {
      toast.error('Failed to archive')
    }
  } catch (error) {
    console.error('Error archiving notification:', error)
    toast.error('Failed to archive')
  }
}

// Enter selection mode
function enterSelectionMode() {
  selectionMode.value = true
  selectedIds.value.clear()
}

// Exit selection mode
function exitSelectionMode() {
  selectionMode.value = false
  selectedIds.value.clear()
}

// Toggle individual notification selection
function toggleNotificationSelection(notificationId) {
  if (selectedIds.value.has(notificationId)) {
    selectedIds.value.delete(notificationId)
  } else {
    selectedIds.value.add(notificationId)
  }
}

// Toggle select all on current page
function toggleSelectAll() {
  if (allCurrentPageSelected.value) {
    // Deselect all current page items
    notifications.value.forEach(n => selectedIds.value.delete(n.id))
  } else {
    // Select all current page items
    notifications.value.forEach(n => selectedIds.value.add(n.id))
  }
}

// Bulk mark as read
async function bulkMarkAsRead() {
  if (selectedIds.value.size === 0) return

  bulkProcessing.value = true
  const idsToProcess = Array.from(selectedIds.value)
  let successCount = 0
  let failCount = 0

  try {
    // Process each notification
    for (const id of idsToProcess) {
      try {
        const response = await User.markNotificationRead(id)
        if (response.status === 'success') {
          successCount++
          // Update local state
          const notification = notifications.value.find(n => n.id === id)
          if (notification) {
            notification.is_read = true
          }
        } else {
          failCount++
        }
      } catch (error) {
        console.error(`Error marking notification ${id} as read:`, error)
        failCount++
      }
    }

    // Update unread count
    const countResponse = await User.getUnreadNotificationCount()
    if (countResponse.status === 'success') {
      mainStore.update_notification_count(countResponse.count)
    }

    // Show result toast
    if (failCount === 0) {
      toast.success(`${successCount} notification${successCount !== 1 ? 's' : ''} marked as read`)
    } else if (successCount === 0) {
      toast.error(`Failed to mark ${failCount} notification${failCount !== 1 ? 's' : ''} as read`)
    } else {
      toast.warning(`${successCount} marked as read, ${failCount} failed`)
    }

    // Exit selection mode
    exitSelectionMode()

  } catch (error) {
    console.error('Bulk mark as read error:', error)
    toast.error('An unexpected error occurred')
  } finally {
    bulkProcessing.value = false
  }
}

// Bulk archive
async function bulkArchive() {
  if (selectedIds.value.size === 0) return

  bulkProcessing.value = true
  const idsToProcess = Array.from(selectedIds.value)
  let successCount = 0
  let failCount = 0

  try {
    // Process each notification
    for (const id of idsToProcess) {
      try {
        const response = await User.archiveNotification(id)
        if (response.status === 'success') {
          successCount++
        } else {
          failCount++
        }
      } catch (error) {
        console.error(`Error archiving notification ${id}:`, error)
        failCount++
      }
    }

    // Show result toast
    if (failCount === 0) {
      toast.success(`${successCount} notification${successCount !== 1 ? 's' : ''} archived`)
    } else if (successCount === 0) {
      toast.error(`Failed to archive ${failCount} notification${failCount !== 1 ? 's' : ''}`)
    } else {
      toast.warning(`${successCount} archived, ${failCount} failed`)
    }

    // Refresh the list to remove archived items (if on active tab)
    resetAndSearch()

    // Exit selection mode
    exitSelectionMode()

  } catch (error) {
    console.error('Bulk archive error:', error)
    toast.error('An unexpected error occurred')
  } finally {
    bulkProcessing.value = false
  }
}

// Setup intersection observer for infinite scroll
const setupIntersectionObserver = () => {
  if (!loadMoreTrigger.value) return

  observer = new IntersectionObserver(
    (entries) => {
      const [entry] = entries
      if (entry.isIntersecting && hasMore.value && !loadingMore.value) {
        fetchNotifications(true)
      }
    },
    {
      root: null,
      rootMargin: '100px',
      threshold: 0.1
    }
  )

  observer.observe(loadMoreTrigger.value)
}

// Handle notification item click (checks selection mode)
function handleNotificationItemClick(notification) {
  if (selectionMode.value) {
    // In selection mode: toggle checkbox
    toggleNotificationSelection(notification.id)
  } else {
    // Normal mode: open slideover
    handleNotificationClick(notification)
  }
}

// Handle notification click (opens slideover and marks as read)
async function handleNotificationClick(notification) {
  // Open slideover immediately
  mainStore.show_slideover(
    NotificationDetail,
    { notification: notification },
    { width: 50 },
    notification.title
  )

  // Mark as read if not already read
  if (!notification.is_read) {
    try {
      const response = await User.markNotificationRead(notification.id)
      if (response.status === 'success') {
        // Update local state
        notification.is_read = true

        // Update unread count
        const countResponse = await User.getUnreadNotificationCount()
        if (countResponse.status === 'success') {
          mainStore.update_notification_count(countResponse.count)
        }
      }
      // Silent failure - don't show error toast
    } catch (error) {
      console.error('Error marking notification as read:', error)
      // Silent failure - don't show error toast
    }
  }
}

// Get notification icon based on severity
function getNotificationIcon(severity) {
  switch (severity) {
    case 0:
      return 'circle-info'
    case 1:
    case 2:
      return 'triangle-exclamation'
    case 3:
    case 4:
      return 'circle-exclamation'
    default:
      return 'bell'
  }
}

// Get notification icon class based on severity
function getNotificationIconClass(severity) {
  switch (severity) {
    case 0:
      return 'icon-info'
    case 1:
      return 'icon-low'
    case 2:
      return 'icon-medium'
    case 3:
      return 'icon-high'
    case 4:
      return 'icon-critical'
    default:
      return 'icon-default'
  }
}

// Lifecycle hooks
onMounted(() => {
  fetchNotifications()

  // Setup observer after initial load
  setTimeout(() => {
    setupIntersectionObserver()
  }, 100)

  // Listen for notification updates
  mainStore.event_on('notification-updated', () => {
    resetAndSearch()
  })
})

onBeforeUnmount(() => {
  if (observer && loadMoreTrigger.value) {
    observer.unobserve(loadMoreTrigger.value)
    observer.disconnect()
  }
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }
})
</script>

<style scoped>
.notifications-page {
  width: 100%;
  height: 100%;
  background: #191f27;
}

.page-container {
  padding: 2rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* Page Header */
.page-header {
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.header-left {
  flex: 1;
}

.page-title {
  font-size: 2rem;
  font-weight: 700;
  color: #f3f4f6;
  margin: 0 0 0.5rem 0;
  font-family: 'Jura', sans-serif;
}

.total-count {
  font-size: 0.875rem;
  color: #9ca3af;
  font-weight: 500;
}

.btn-mark-all-read {
  padding: 0.75rem 1.5rem;
  background: rgba(168, 85, 247, 0.15);
  border: 1px solid rgba(168, 85, 247, 0.3);
  border-radius: 0.5rem;
  color: #a855f7;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-mark-all-read:hover:not(:disabled) {
  background: rgba(168, 85, 247, 0.25);
  border-color: rgba(168, 85, 247, 0.5);
}

.btn-mark-all-read:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Filter Tabs */
.filter-tabs {
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.filter-tabs-left {
  display: flex;
  gap: 0.5rem;
}

.tab-button {
  padding: 0.5rem 1rem;
  background-color: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem;
  color: #6B7280;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.tab-button:hover {
  background-color: rgba(255, 255, 255, 0.05);
  color: #E5E7EB;
}

.tab-button.active {
  background-color: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.2);
  color: #FFFFFF;
}

/* Search Input */
.search-wrapper {
  flex-shrink: 0;
  position: relative;
  margin-bottom: 1.5rem;
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  font-size: 1rem;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  background: rgba(26, 29, 36, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.5rem;
  color: #f3f4f6;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.search-input:focus {
  outline: none;
  border-color: rgba(168, 85, 247, 0.3);
  background: rgba(26, 29, 36, 0.6);
}

.search-input::placeholder {
  color: #6b7280;
}

/* Loading State */
.loading-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  min-height: 0;
}

.spinner {
  font-size: 2.5rem;
  color: #a855f7;
  margin-bottom: 1rem;
}

.loading-text {
  color: #9ca3af;
  font-size: 0.875rem;
  margin: 0;
}

/* Error State */
.error-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  min-height: 0;
}

.error-icon {
  font-size: 3rem;
  color: #ef4444;
  margin-bottom: 1rem;
  opacity: 0.8;
}

.error-title {
  font-size: 1rem;
  font-weight: 600;
  color: #ef4444;
  margin: 0 0 0.5rem 0;
}

.error-message {
  font-size: 0.875rem;
  color: #9ca3af;
  margin: 0 0 1.5rem 0;
  text-align: center;
}

.retry-button {
  padding: 0.625rem 1.5rem;
  background: rgba(168, 85, 247, 0.15);
  border: 1px solid rgba(168, 85, 247, 0.3);
  border-radius: 0.5rem;
  color: #a855f7;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.retry-button:hover {
  background: rgba(168, 85, 247, 0.25);
  border-color: rgba(168, 85, 247, 0.5);
}

/* Empty State */
.empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  min-height: 0;
}

.empty-icon {
  font-size: 3rem;
  color: #6b7280;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-title {
  font-size: 1rem;
  font-weight: 600;
  color: #9ca3af;
  margin: 0 0 0.5rem 0;
}

.empty-description {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
  text-align: center;
}

/* Notifications List */
.notifications-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.notification-grid {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 2rem;
}

/* Notification Row Container */
.notification-row {
  transition: all 0.2s ease;
}

.notification-row.selected .notification-item {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.2);
}

.notification-item.selectable {
  cursor: pointer;
}

/* Notification Item */
.notification-item {
  flex: 1;
  background: rgba(26, 29, 36, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.5rem;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  position: relative;
}

.notification-item:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.15);
}

.notification-item.selectable:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.2);
}

.notification-item.unread {
  border-left: 3px solid #a855f7;
  background: rgba(168, 85, 247, 0.05);
}

.notification-icon {
  flex-shrink: 0;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  background: rgba(255, 255, 255, 0.05);
  font-size: 1.25rem;
}

.icon-info {
  color: #3b82f6;
}

.icon-low {
  color: #10b981;
}

.icon-medium {
  color: #f59e0b;
}

.icon-high {
  color: #f97316;
}

.icon-critical {
  color: #ef4444;
}

.icon-default {
  color: #a855f7;
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.notification-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #f3f4f6;
  line-height: 1.4;
}

.notification-time {
  font-size: 0.75rem;
  color: #6b7280;
  white-space: nowrap;
  flex-shrink: 0;
}

.notification-message {
  font-size: 0.875rem;
  color: #9ca3af;
  line-height: 1.5;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.unread-indicator {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 8px;
  height: 8px;
  background: #a855f7;
  border-radius: 50%;
}

/* Load More Trigger */
.load-more-trigger {
  height: 20px;
  margin: 20px 0;
}

/* Loading More */
.loading-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1.5rem;
  color: #9ca3af;
  font-size: 0.875rem;
}

.spinner-small {
  font-size: 1rem;
  color: #a855f7;
}

/* Header Actions */
.header-actions {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

/* Select Button */
.btn-select {
  padding: 0.5rem 1rem;
  background-color: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem;
  color: #6B7280;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-select:hover {
  background-color: rgba(255, 255, 255, 0.05);
  color: #E5E7EB;
}

/* Selection Bar */
.selection-bar {
  flex-shrink: 0;
  padding: 0.625rem 1rem;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.5rem;
  margin-bottom: 0.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.selection-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.selection-count {
  color: #9ca3af;
  font-size: 0.875rem;
  font-weight: 500;
}

.btn-select-all {
  padding: 0.5rem 1rem;
  background-color: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem;
  color: #6B7280;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-select-all:hover {
  background-color: rgba(255, 255, 255, 0.05);
  color: #E5E7EB;
}

.bulk-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-bulk-action {
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background-color: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #6B7280;
}

.btn-bulk-action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-bulk-action:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.05);
  color: #E5E7EB;
}

.btn-mark-read,
.btn-archive,
.btn-cancel {
  /* Inherits from btn-bulk-action */
}

/* Quick Action Buttons */
.notification-actions {
  position: absolute;
  top: 50%;
  right: 1rem;
  transform: translateY(-50%);
  display: flex;
  gap: 0.5rem;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
}

.notification-item:hover .notification-actions {
  opacity: 1;
  pointer-events: auto;
}

.btn-quick-action {
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #1a1d24;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 0.5rem;
  color: #9ca3af;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-quick-action:hover {
  background-color: #252930;
  border-color: rgba(255, 255, 255, 0.3);
  color: #f3f4f6;
}

/* Responsive Design */
@media (max-width: 768px) {
  .page-container {
    padding: 1.5rem;
  }

  .page-header {
    flex-direction: column;
    gap: 1rem;
  }

  .page-title {
    font-size: 1.5rem;
  }

  .notification-item {
    padding: 0.875rem;
  }

  .notification-icon {
    width: 2rem;
    height: 2rem;
    font-size: 1rem;
  }

  .notification-header {
    flex-direction: column;
    gap: 0.25rem;
  }

  .notification-time {
    align-self: flex-start;
  }

  .filter-tabs {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }

  .filter-tabs-left {
    width: 100%;
  }

  .btn-select {
    width: 100%;
    justify-content: center;
  }

  .selection-bar {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }

  .selection-left {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }

  .selection-count {
    text-align: center;
  }

  .btn-select-all {
    width: 100%;
    justify-content: center;
  }

  .bulk-actions {
    flex-direction: column;
  }

  .btn-bulk-action {
    width: 100%;
    justify-content: center;
  }
}
</style>
